import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
export { AuthContext };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest, obj=auth

  const check = useCallback(async () => {
    // SECURITY + CORRECTNESS (hardening gate Phase 20): this used to gate
    // on a plain-text session token mirrored into localStorage
    // ("vyom_token"), which (a) sat somewhere any XSS on this page could
    // read it, entirely defeating the httpOnly session cookie's
    // protection, and (b) meant a cleared/absent localStorage entry was
    // wrongly treated as "definitely signed out" even when a still-valid
    // httpOnly cookie existed. /auth/me (sent with the cookie automatically
    // via api.js's withCredentials:true) is the actual, authoritative
    // signal -- exactly like ClerkAuthProvider's own sync() already does
    // it. No functional change for a real session: the backend already
    // reads the cookie before ever looking at a Bearer header
    // (core/security.py::get_current_user).
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = async (email, password) => {
    try {
      // SECURITY (hardening gate Phase 20): the response also includes the
      // raw token as data.token, but it no longer needs to be mirrored
      // into localStorage -- this same response already set the real
      // session as an httpOnly cookie (routes/auth.py::_set_cookie), which
      // every request already carries via api.js's withCredentials:true.
      // Storing a plain-text copy of it in localStorage served no purpose
      // this app actually needs and left it readable by any XSS.
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      // e.response is only set when the backend was reached and answered
      // with an HTTP status; its absence means the request never got a
      // response at all -- wrong REACT_APP_BACKEND_URL/port, the backend
      // isn't running, or a CORS/network failure. That case must not be
      // reported as the same generic string a real 401 gets, or a
      // misconfigured local backend URL becomes indistinguishable from an
      // actual bad password.
      if (!e.response) {
        return {
          ok: false,
          error: `Unable to reach the Vyom server at ${API}. Check that the backend is running and REACT_APP_BACKEND_URL is correct.`,
        };
      }
      return { ok: false, error: formatApiError(e.response.data?.detail) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { console.warn("[Auth] logout request failed (clearing session anyway)", e); }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: check }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
